/**
 * Everything in the database, one kind at a time.
 *
 * A column beside the object on wide screens, and the body of the "Open"
 * sheet on narrow ones — the same list either way. The three kinds are
 * Postgres's own division: a table holds rows, a view is a saved query, a
 * function belongs to the database rather than to anything in it. Each row
 * says which it is on the right — a row count, `view`/`mat.`, a language.
 *
 * A search that matches nothing offers to create the thing searched for, with
 * the name already in the statement.
 */
import { AppDatabaseFunction, AppDatabaseTable } from '@back4app2/sdk';
import { DsButton, SegmentedControl } from '../../components/ds';
import { ColumnsIcon, PanelLeftIcon, PlusIcon, SearchIcon, TerminalIcon } from '../../components/ds/icons';
import { formatApproxCount } from './cells';
import { SearchField } from './controls';

export type ObjectKind = 'table' | 'view' | 'function';

/** A table's or view's id: `schema.name`. */
export const tableId = (table: { schema: string, name: string }) =>
  `${table.schema}.${table.name}`;

/**
 * A function's id in the same namespace as a table's, and told apart by the
 * prefix: `fn:` cannot collide with `schema.table`, and the arguments are
 * part of it because in Postgres two functions may share a name.
 */
export const functionId = (fn: { schema: string, name: string, arguments: string }) =>
  `fn:${fn.schema}.${fn.name}(${fn.arguments})`;

export interface ExplorerItem {
  id: string;
  name: string;
  meta: string;
}

export const KIND_ICON: Record<ObjectKind, (props: { size?: number, className?: string }) => JSX.Element> = {
  table: ColumnsIcon,
  view: PanelLeftIcon,
  function: TerminalIcon,
};

const NOUN: Record<ObjectKind, string> = { table: 'tables', view: 'views', function: 'functions' };

export const explorerItems = (
  tables: AppDatabaseTable[],
  functions: AppDatabaseFunction[]
): Record<ObjectKind, ExplorerItem[]> => ({
  table: tables
    .filter(table => table.kind === 'table')
    .map(table => ({
      id: tableId(table),
      name: table.schema === 'public' ? table.name : `${table.schema}.${table.name}`,
      meta: formatApproxCount(table.estimatedRowCount),
    })),
  // Views and materialized views are one group: both are a saved query, and
  // whether the rows are stored is said on the row itself.
  view: tables
    .filter(table => table.kind !== 'table')
    .map(table => ({
      id: tableId(table),
      name: table.schema === 'public' ? table.name : `${table.schema}.${table.name}`,
      meta: table.kind === 'materialized view' ? 'mat.' : 'view',
    })),
  function: functions.map(fn => ({
    id: functionId(fn),
    name: fn.name,
    meta: fn.returns === 'trigger' ? 'trigger' : fn.language,
  })),
});

const KIND_LABEL: Record<ObjectKind, string> = {
  table: 'Tables',
  view: 'Views',
  function: 'Functions',
};

const ORDER: ObjectKind[] = ['table', 'view', 'function'];

/**
 * What a search matches, and in what order.
 *
 * A name that STARTS with what was typed comes before one that merely contains
 * it — typing "ord" should put `orders` above `work_orders` — and a match on
 * the schema-qualified name counts too, so `auth.users` is found by "auth".
 */
const rank = (item: ExplorerItem, term: string) => {
  const name = item.name.toLowerCase();

  return name.startsWith(term) ? 0 : name.includes(term) ? 1 : 2;
};

const matches = (items: ExplorerItem[], term: string) => (
  term
    ? items
      .filter(item => rank(item, term) < 2)
      .sort((a, b) => rank(a, term) - rank(b, term))
    : items
);

const DatabaseExplorer = ({ items, kind, query, selectedId, isTouch, onQuery, onKind, onSelect, onNew }: {
  items: Record<ObjectKind, ExplorerItem[]>,
  kind: ObjectKind,
  query: string,
  selectedId?: string,
  /** The sheet on a phone: 44px targets. */
  isTouch?: boolean,
  onQuery: (query: string) => void,
  onKind: (kind: ObjectKind) => void,
  onSelect: (id: string) => void,
  /** Opens the create statement for this kind, named after the search if given. */
  onNew: (kind: ObjectKind, name?: string) => void,
}) => {
  const term = query.trim().toLowerCase();
  // With nothing typed the rail lists the open kind, as it always did. A search
  // looks at all three: nobody has to know which tab a name lives in to find
  // it, and searching "orders" with Functions open used to answer "no match"
  // while the table sat one tab away.
  const groups = (term ? [kind, ...ORDER.filter(value => value !== kind)] : [kind])
    .map(value => ({ kind: value, items: matches(items[value], term) }))
    .filter(group => group.items.length > 0);
  const found = groups.reduce((sum, group) => sum + group.items.length, 0);
  // Headed whenever the answer is not simply "the open kind": more than one
  // group, or a single group of something else — a lone function found from
  // the Tables tab has to say that it is a function.
  const isGrouped = groups.length > 1 || groups.some(group => group.kind !== kind);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* The search heads the rail: the schema line named something nobody can
          change from here, and pushed the one control that is used every time
          a table is opened down a row. */}
      {/* A narrower inset than the rest of the screen, on purpose: the rail is
          232px and the three-way control across it is what the width is for.
          It also lines the search and the segmented up with the rows below. */}
      <div className={`flex flex-col gap-3 border-b border-navy-700 ${
        isTouch ? 'px-4 pt-4 pb-3.5' : 'px-3 pt-5 pb-4 gap-4'
      }`}>
        <SearchField
          value={query}
          onChange={event => onQuery(event.target.value)}
          placeholder="Search this database"
          aria-label="Search tables, views and functions"
          prefix={<SearchIcon size={16} />}
        />
        <SegmentedControl
          segments={[
            { value: 'table', label: 'Tables' },
            { value: 'view', label: 'Views' },
            { value: 'function', label: 'Functions' },
          ]}
          value={kind}
          onChange={onKind}
          size={isTouch ? 'touch' : 'sm'}
          ariaLabel="Kind of object"
          // Three labels in a 232px rail: the default 11px of padding a side
          // pushed "Functions" out through the right edge. They share the width
          // evenly and set a notch down instead of overflowing.
          className={`w-full [&>button]:flex-1 [&>button]:min-w-0 [&>button]:truncate ${
            isTouch ? '' : '[&>button]:px-0 [&>button]:text-ds-115'
          }`}
        />
      </div>

      <div className={`flex-1 min-h-0 overflow-auto b4-thin flex flex-col gap-1 ${
        isTouch ? 'px-4 pt-2.5 pb-4 gap-1.5' : 'px-3 pt-3 pb-4'
      }`}>
        {groups.map(group => (
          <div key={group.kind} className="flex flex-col gap-1">
            {isGrouped ? (
              <span className={`flex items-baseline justify-between gap-2 pt-2 pb-1 font-courrier text-ds-105 uppercase tracking-[0.14em] leading-none text-navy-300 ${
                isTouch ? 'px-3.5' : 'px-3'
              }`}>
                {KIND_LABEL[group.kind]}
                <span className="flex-none normal-case tracking-normal">{group.items.length}</span>
              </span>
            ) : null}
            {group.items.map(item => {
              const isActive = item.id === selectedId;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelect(item.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={[
                    // The name is machine-written, so it is set in the mono
                    // face — and the kind icon is gone: a row says which kind
                    // it is by the group it sits in, and the icon cost the
                    // name its width.
                    'b4-focus flex items-center justify-between gap-2.5 rounded-[6px] text-left font-courrier text-ds-125 transition-colors duration-[120ms]',
                    // An explicit height, not `min-h-12`: this Tailwind build
                    // has no numeric min-height scale, so the class was doing
                    // nothing and the rows were as tall as their text.
                    isTouch ? 'min-h-[48px] px-3.5 py-2' : 'min-h-[34px] px-3',
                    isActive
                      ? 'bg-navy-600 text-white'
                      : 'text-navy-100 hover:text-white hover:bg-navy-600/50',
                  ].join(' ')}
                >
                  <span className="min-w-0 truncate">{item.name}</span>
                  <span className="flex-none font-courrier text-ds-115 text-navy-300">{item.meta}</span>
                </button>
              );
            })}
          </div>
        ))}

        {found === 0 ? (
          <div className="flex flex-col items-stretch gap-2.5 p-2.5">
            <span className="text-ds-135 text-navy-300">
              {term ? `No match for “${query.trim()}”.` : `No ${NOUN[kind]} yet.`}
            </span>
            {term ? (
              <DsButton variant="subtle" size={isTouch ? 'touch' : 'md'} onClick={() => onNew(kind, query.trim())}>
                <PlusIcon size={16} /> Create {kind} {query.trim()}
              </DsButton>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className={`flex flex-col border-t border-navy-500 ${isTouch ? 'px-4 py-3.5' : 'px-3.5 py-3.5'}`}>
        <DsButton variant="subtle" size={isTouch ? 'touch' : 'md'} onClick={() => onNew(kind)} className="w-full">
          <PlusIcon size={16} /> New {kind}
        </DsButton>
      </div>
    </div>
  );
};

export default DatabaseExplorer;
